import React, { useState, useEffect, useRef } from 'react';
import SupplyChainContract from '../../contracts/SupplyChainContract';
import QRCodeCanvas from '../../components/QRCodeCanvas/QRCodeCanvas'; 

const FarmerPage = () => {
  const [quantity, setQuantity] = useState('');
  const [gradingResults, setGradingResults] = useState('');
  const [countryOfOrigin, setCountryOfOrigin] = useState('');
  const [batchIdForSearch, setBatchIdForSearch] = useState('');
  const [searchedVcData, setSearchedVcData] = useState(null);
  const [farmerVCs, setFarmerVCs] = useState([]);
  const [qrCodeData, setQrCodeData] = useState(null);
  const qrCodeCanvasRef = useRef(null);
  const [searchInput, setSearchInput] = useState('');

  useEffect(() => {
    fetchFarmerVCs();
  }, []);

  const generateQRCode = (batchID) => {
    const qrCodeData = batchID.toString();
    setQrCodeData(qrCodeData);
  };

  const downloadQRCode = () => {
    const canvas = qrCodeCanvasRef.current;
    if (!canvas) {
      console.error('Canvas element not found');
      return;
    }

    const qrCodeURL = canvas.toDataURL('image/png');
    const link = document.createElement('a');
    link.href = qrCodeURL;
    link.download = 'FarmerVC_QRCode.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleCreateVC = async () => {
    try {
      await SupplyChainContract.methods
        .createFarmerVC(quantity, gradingResults, countryOfOrigin)
        .send({ from: window.ethereum.selectedAddress });
  
      alert('VC created successfully!');
      fetchFarmerVCs();
  
      const latestBatchId = await SupplyChainContract.methods.getTotalFarmerVCs().call();
      setBatchIdForSearch(latestBatchId);
      generateQRCode(latestBatchId);
    } catch (error) {
      console.error('Error creating VC:', error);
    }
  };

  const handleSearchByBatchId = async () => {
    try {
      const matchingVc = await SupplyChainContract.methods.getFarmerVCByBatchId(batchIdForSearch).call();

      if (matchingVc.batchID !== 0) {
        setSearchedVcData(matchingVc);
      } else {
        setSearchedVcData(null);
        console.log('No VC found with the specified BatchID.');
      }
    } catch (error) {
      console.error('Error fetching FarmerVC:', error);
    }
  };

  const fetchFarmerVCs = async () => {
    try {
      const totalFarmerVCs = await SupplyChainContract.methods.getTotalFarmerVCs().call();

      const farmerVCList = [];
      for (let i = 1; i <= totalFarmerVCs; i++) {
        const farmerVC = await SupplyChainContract.methods.getFarmerVCByBatchId(i).call();
        farmerVCList.push(farmerVC);
      }

      setFarmerVCs(farmerVCList);
    } catch (error) {
      console.error('Error fetching FarmerVCs:', error);
    }
  };

  const convertEpochToDateTime = (epoch) => {
    const date = new Date(epoch * 1000);
    return date.toLocaleString();
  };

  return (
    <div className="container mx-auto p-8 pt-16">
      {/* Create VC Form */}
      <div className="mb-8">
        <h3 className="text-2xl font-semibold mb-4">Create Meat Processor Verifiable Credential</h3>
        <div className="mb-4">
          <label className="mr-4 text-lg">Quantity:</label>
          <input type="text" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="border p-2" />
        </div>
        <div className="mb-4">
          <label className="mr-4 text-lg">Grading Results:</label>
          <input type="text" value={gradingResults} onChange={(e) => setGradingResults(e.target.value)} className="border p-2" />
        </div>
        <div className="mb-4">
          <label className="mr-4 text-lg">Country of Origin:</label>
          <select
            value={countryOfOrigin}
            onChange={(e) => setCountryOfOrigin(e.target.value)}
            className="border p-2"
          >
            <option value="">Select Country</option>
            <option value="US">US</option>
            <option value="EU">EU</option>
            <option value="Australia">Australia</option>
            <option value="NewZealand">New Zealand</option>
            <option value="Brazil">Brazil</option>
            <option value="Japan">Japan</option>
          </select>
        </div>
        <button onClick={handleCreateVC} className="bg-blue-500 text-white px-4 py-2 rounded">
          Create VC
        </button>
      </div>

      {/* QR Code Section */}
      {qrCodeData && (
        <div className="mb-8">
          <h3 className="text-2xl font-semibold mb-4">QR Code</h3>
          <QRCodeCanvas value={qrCodeData} size={256} canvasRef={qrCodeCanvasRef} />
          <button onClick={downloadQRCode} className="bg-blue-500 text-white px-4 py-2 rounded mt-4">
            Download QR Code
          </button>
        </div>
      )}

      {/* Search by BatchID Form */}
      <div className="mb-8">
        <h3 className="text-2xl font-semibold mb-4">Search Meat Processor VC</h3>
        <div className="flex items-center mb-4">
          <label className="mr-4 text-lg">BatchID:</label>
          <input
            type="text"
            value={batchIdForSearch}
            onChange={(e) => setBatchIdForSearch(e.target.value)}
            className="border p-2 mr-2"
          />
          <button onClick={handleSearchByBatchId} className="bg-blue-500 text-white px-4 py-2 rounded">Search</button>
        </div>
      </div>

      {/* Display searched VC data */}
      {searchedVcData && (
        <div className="mb-8">
          <h3 className="text-2xl font-semibold mb-4">Search Result</h3>
          <p className="text-lg">Batch ID: {searchedVcData.batchID}</p>
          <p className="text-lg">Quantity: {searchedVcData.quantity}</p>
          <p className="text-lg">Grading Results: {searchedVcData.gradingResults}</p>
          <p className="text-lg">Country of Origin: {searchedVcData.countryOfOrigin}</p>
          <p className="text-lg">Date of Production: {convertEpochToDateTime(searchedVcData.dateOfProduction)}</p>
          <p className="text-lg">Expiry Date: {convertEpochToDateTime(searchedVcData.expiryDate)}</p>
        </div>
      )}

      {/* FarmerVCs Table */}
      <div>
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-semibold">Verifiable Credentials List</h3>
          <div className="flex items-center">
            <label className="mr-4 text-lg">Search:</label>
            <input
              type="text"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="border p-2"
            />
          </div>
        </div>

        <table className="w-full border">
        <thead>
          <tr className="bg-gray-200">
            <th className="border p-2 text-center" rowSpan="2">BatchID</th>
            <th className="border p-2 text-center" colSpan="5" style={{ backgroundColor: '#6EE7B7' }}>Meat Processor</th>
          </tr>
          <tr className="bg-gray-200">
            <th className="border p-2 text-center bg-green-200">Quantity</th>
            <th className="border p-2 text-center bg-green-200">Grading Results</th>
            <th className="border p-2 text-center bg-green-200">Country of Origin</th>
            <th className="border p-2 text-center bg-green-200">Date of Production</th>
            <th className="border p-2 text-center bg-green-200">Expiry Date</th>
          </tr>
        </thead>
          <tbody>
            {farmerVCs
              .filter((vc) =>
                String(vc.batchID).toLowerCase() === searchInput.toLowerCase() ||
                String(vc.gradingResults).toLowerCase().includes(searchInput.toLowerCase())
              )
              .map((vc) => (
                <tr key={vc.batchID} className="bg-gray-100">
                  <td className="border p-2 text-center">{vc.batchID}</td>
                  <td className="border p-2 text-center bg-green-200">{vc.quantity}</td>
                  <td className="border p-2 text-center bg-green-200">{vc.gradingResults}</td>
                  <td className="border p-2 text-center bg-green-200">{vc.countryOfOrigin}</td>
                  <td className="border p-2 text-center bg-green-200">{convertEpochToDateTime(vc.dateOfProduction)}</td>
                  <td className="border p-2 text-center bg-green-200">{convertEpochToDateTime(vc.expiryDate)}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};


export default FarmerPage;
